// Reception remainders: lost-card status and replacement eligibility (PB-06-12), returning-visitor
// evidence expiry (PB-06-04) and per-participant cancellation impact (PB-04-07). Scope-safe shapes:
// states and dates only — never document numbers, provider references or reasons from restricted
// modules.
import { z } from 'zod';

export const ReturningEvidenceQuery = z.object({ siteId: z.uuid(), personId: z.uuid() });

export type EvidenceExpiryStateDto = 'expired' | 'expiring' | 'valid' | 'no_expiry';

export interface ReturningEvidenceResponse {
  readonly personId: string;
  /** Site calendar date the states were computed for (YYYY-MM-DD). */
  readonly asOf: string;
  readonly soonDays: number;
  /** Module 08: the latest identity record of each earlier visit (newest first, at most 5). */
  readonly identity: ReadonlyArray<{
    readonly method: string;
    readonly documentType: string | null;
    readonly status: string;
    readonly expiresOn: string | null;
    readonly state: EvidenceExpiryStateDto;
    readonly capturedOn: string;
  }>;
  /** Module 09: the person's current contractor evidence (person and affiliation subjects). */
  readonly contractor: ReadonlyArray<{
    readonly typeCode: string;
    readonly typeLabel: { readonly en: string; readonly ar?: string | undefined } | null;
    readonly reviewStatus: string;
    readonly expiresOn: string | null;
    readonly state: EvidenceExpiryStateDto;
  }>;
}

export type ReplacementStateDto = 'allowed' | 'restricted' | 'escalated';

export interface LostCardStatusResponse {
  readonly participationId: string;
  readonly presenceStatus: string;
  /** Cards this participation reported lost (newest first). Physical custody only. */
  readonly lostCards: ReadonlyArray<{ readonly cardId: string; readonly number: string; readonly at: string; readonly custody: string }>;
  /** Logical credentials and their revocation state; null when the caller may not read credentials. */
  readonly credentials: ReadonlyArray<{
    readonly id: string;
    readonly tokenType: string;
    readonly state: string;
    readonly revokeReason: string | null;
    /** An unknown/failed provider operation waits in the recovery queue (PB-11-09). */
    readonly inRecoveryQueue: boolean;
  }> | null;
  readonly replacement: { readonly state: ReplacementStateDto; readonly reason: 'credential_exists' | 'prior_unresolved' | null };
}

export interface CancelImpactResponse {
  readonly visitId: string;
  readonly planningStatus: string;
  readonly participants: ReadonlyArray<{
    readonly participationId: string;
    readonly displayName: string;
    readonly presenceStatus: string;
    readonly credentialStatus: string;
    readonly onsite: boolean;
    readonly liveCredentials: number;
    readonly linksRevoked: number;
    readonly removed: boolean;
  }>;
  readonly totals: { readonly onsite: number; readonly liveCredentials: number; readonly linksRevoked: number };
}
